import { LocalizedText } from "../../components/LocalizedText";
import './CardReward.css';

import * as React from 'react';
import { JButton } from '../../components/UI/JButton';
import { useAppSelector } from '../../hooks';
import { IItem } from '../../slices/inventorySlice';
import { Item } from '../../components/Item';
import { CenterContainer } from '../../components/CenterContainer/CenterContainer';
import useTranslator from '../../hooks/translate';
import { Backdrop } from '../../components/Backdrop';
import { CDNImage } from '../../components/Elements/Image';
import { prompt } from '../../util/EventEmitter';
import { cancelFromServer, fromServer, toServer } from '../../util/ServerSocket';

function isCardItem(value: any): value is IItem {
  return !!value && Number.isSafeInteger(value.iid) && typeof value.uid === 'string' && Array.isArray(value.core) && !!value.props;
}

export function CardReward() {
  const t = useTranslator();
  const cbRef = React.useRef<() => void>();

  const fight = useAppSelector((store) => store.fight.show);

  const [show, setShow] = React.useState(false);
  const [items, setItems] = React.useState<(IItem | null)[]>([]);
  const [selected, setSelected] = React.useState(-1);
  const [pending, setPending] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const busy = React.useRef(false);
  const round = React.useRef(0);
  const timeout = React.useRef<number>();
  const requestId = React.useRef(0);

  function requestCard(event: string, idx: number) {
    if (busy.current || selected !== -1) return;
    busy.current = true; setPending(true); setMessage('');
    const currentRound = round.current;
    const currentRequest = ++requestId.current;
    timeout.current = window.setTimeout(() => {
      if (currentRound !== round.current || currentRequest !== requestId.current) return;
      busy.current = false; setPending(false); setMessage('ยังไม่ได้รับคำตอบจากเซิร์ฟเวอร์ กรุณาลองใหม่');
    }, 10000);
    toServer(event, idx, (response: any) => {
      if (currentRound !== round.current || currentRequest !== requestId.current) return;
      window.clearTimeout(timeout.current); busy.current = false; setPending(false);
      if (event === 'cardLook' && isCardItem(response)) {
        setItems(previous => previous.map((item, index) => index === idx ? response : item));
      } else if (event === 'cardSelect' && Array.isArray(response) && response.length === items.length && response.length > 0 && response.every(isCardItem)) {
        setItems(response); setSelected(idx);
      } else {
        setMessage(typeof response?.error === 'string' ? response.error : 'แสดงการ์ดไม่สำเร็จ กรุณาลองใหม่');
      }
    });
  }

  React.useEffect(() => {
    fromServer('showCard', (data, cb) => {
      round.current++; window.clearTimeout(timeout.current); busy.current = false; setPending(false); setMessage('');
      if (!Array.isArray(data) || !data.length) { setShow(false); return; }
      setShow(true);
      setItems(data.map(item => isCardItem(item) ? item : null));
      setSelected(-1);
      cbRef.current = cb;
    });
    toServer('cardCheck');

    return () => {
      cancelFromServer('showCard');
      round.current++; window.clearTimeout(timeout.current);
    };
  }, []);

  if (!show || fight) return null;

  return (
    <>
      <Backdrop />
      <CenterContainer>
        {message && <div role="alert" style={{ position: 'absolute', top: 150, left: '50%', transform: 'translateX(-50%)', width: 650, textAlign: 'center', color: '#fff', background: '#8b2534', padding: 8, zIndex: 20 }}>{message}</div>}
        <div
          className="outline"
          style={{
            position: 'absolute',
            top: '-150px',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 1000,
            textAlign: 'center',
            fontFamily: 'blippo',
            fontSize: 32,
            color: 'yellow',
            userSelect: 'none',
            pointerEvents: 'none',
          }}><LocalizedText text={"Click on a green card to get a free reward!"} /></div>
        <div
          className="outline"
          style={{
            position: 'absolute',
            top: '-125px',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 1000,
            textAlign: 'center',
            fontFamily: 'blippo',
            fontSize: 18,
            color: 'yellow',
            userSelect: 'none',
            pointerEvents: 'none',
          }}><LocalizedText text={"~Or pay for a sneak peak~"} /></div>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', gap: 10 }}>
          {items.map((item, idx) => (
            <div key={`card-${idx}`}>
              <div style={{ position: 'relative' }}>
                <CDNImage
                  src={`ui/UIResource/OutSearch/${selected === -1 ? 'Poke' : 'CardFlipped'}.png`}
                  style={{ cursor: selected === -1 ? 'pointer' : 'default' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (selected !== -1 || pending) {
                      return;
                    }

                    requestCard('cardSelect', idx);
                  }}
                />

                {isCardItem(item) && (
                  <>
                    <Item
                      item={items[idx]!}
                      style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) translateY(-12px)' }}
                      noBackground
                      readOnly
                    />
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 17,
                        left: 15,
                        fontSize: '0.8rem',
                        fontFamily: 'Arial',
                        width: 80,
                        height: 17,
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={t(`item__${item.iid}--name`)}>
                      {t(`item__${item.iid}--name`)}
                    </div>
                  </>
                )}
              </div>

              {item === null && (
                <JButton
                  size={{ width: 75, height: 21 }}
                  text={'Look'}
                  disabled={pending}
                  style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: 5 }}
                  onClick={() => {
                    prompt('View contents of the card for 10,000 stones?', () => {
                      requestCard('cardLook', idx);
                    });
                  }}
                />
              )}

              {item !== null && selected === idx && (
                <JButton
                  size={{ width: 75, height: 21 }}
                  text={'Collect'}
                  style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: 5 }}
                  onClick={() => {
                    setShow(false);
                    cbRef.current?.();
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </CenterContainer>
    </>
  );
}
